import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
    Button,
    TextField,
    CircularProgress,
    Grid,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Paper,
    PaperProps
} from "@mui/material";
import { useState, useEffect } from "react";
import { useDataProvider, useRefresh, useNotify } from "react-admin";
import { useForm } from "react-hook-form";
import ApiResources from "src/providers/ApiResources";
import Draggable from 'react-draggable';
import { CustomerRoleEnum, AdminRoleEnum } from "../../common/enum/RoleEnum";
import { SelectField, AutocompleteInput} from "src/components/input";

interface VerifyModalProps {
    record: any;
    open: boolean;
    setOpen: any;
}

const PaperComponent = (props: PaperProps) => {
    return (
        <Draggable
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}
  
export const VerifyModal = (props: VerifyModalProps) => {
    const { record, open, setOpen } = props;

    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const { register, control, handleSubmit, getValues, setValue, setError, formState: { errors }} = useForm({ 
        defaultValues: { 
            customer_roles: record?.customer_roles,
            admin_roles: record?.admin_roles,
        } 
    });
    const dataProvider = useDataProvider();
    const refresh = useRefresh();
    const notify = useNotify();

    const executeVerify = async (data) => {
        try {
            let formData = {...data};

            let roles = [...data.customer_roles, ...data.admin_roles];
            delete formData["customer_roles"]; 
            delete formData["admin_roles"]; 

            setLoading(true);
            setSubmitError(null);

            dataProvider
            .patch(ApiResources.verifyUser(record.id, false), {
                data: {...formData, roles: roles},
                previousData: record,
            })
            .then((res) => {
                notify('User has been verified.');
                setLoading(false);
                refresh();
                setOpen(false);
            })
            .catch((error) => {
                setSubmitError(error.message);
                setLoading(false);
            });
        } catch (e) {
            console.log(e);
        }
    }

    return (
        <>
        <Dialog
            onClose={() => setOpen(false)}
            open={open}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            fullWidth={true}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(executeVerify)}>
            <DialogTitle sx={{padding: "18px"}}>
                <Typography variant="body1" sx={{fontSize: "20px", fontWeight: "500"}}>{`Verify ${record.first_name} ${record.last_name} (${record.email})`}</Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => setOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{padding: "18px !important"}}>
                <Grid container item sm={12} rowSpacing={1} columnSpacing={2}>
                    <Grid item sm={12} md={12}>
                        <Grid container rowSpacing={1} columnSpacing={2}>
                            <Grid item md={6}>
                                <SelectField 
                                    multiple 
                                    size="small" 
                                    label="Customer Roles" 
                                    choices={CustomerRoleEnum} 
                                    {...register("customer_roles")}
                                    onChange={(e) => {
                                        setValue(e.target?.name, e.target?.value, { shouldValidate: true });
                                    }}
                                    value={getValues("customer_roles") || []}
                                />
                            </Grid>
                            <Grid item md={6}>
                                <SelectField 
                                    multiple 
                                    size="small" 
                                    label="Admin Roles" 
                                    choices={AdminRoleEnum} 
                                    {...register("admin_roles")}
                                    onChange={(e) => {
                                        setValue(e.target?.name, e.target?.value, { shouldValidate: true });
                                    }}
                                    value={getValues("admin_roles") || []}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    { submitError &&
                    <Grid item sm={12} md={12} sx={{display: "flex", marginTop: "18px"}}>
                        <Typography variant="subtitle2" color="error">{submitError}</Typography>
                    </Grid>                     
                    } 
                </Grid>
            </DialogContent>
            <DialogActions sx={{padding: "18px"}}>
                <Button
                    variant="outlined"
                    onClick={() => {
                        setOpen(false);
                    }}
                >
                    Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={loading}>
                    {loading && <CircularProgress size={18} thickness={2} sx={{ fontSize: "18px", color: "#ffffff", marginRight: "8px"}}/>}
                    Verify
                </Button>
            </DialogActions>
            </form>
        </Dialog>
        </>
    );
};
